import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShip,
  faHotel,
  faHome,
  faCheck,
  faClock,
  faArrowRight,
  faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons';
import type { ILandingPageRenderData } from '@share/common-types';
import { LANDING_DEFAULT_HERO_IMAGE, LANDING_DEFAULT_PRODUCT_IMAGES } from './landing-page-default-assets';
import { ExperiencesGridSection } from './experiences-grid-section';
import { TestimonialsSection } from './testimonials-section';
import { CruiseLinesSection } from './cruise-lines-section';
import styles from './layout-immersive.module.scss';

interface LayoutImmersiveProps {
  data: ILandingPageRenderData;
}

/** Default amenity copy for the blue “Everything You Need” block (checklist). */
const DEFAULT_RESORT_AMENITY_LINES: string[] = [
  'Accommodations in a deluxe room or suite',
  'Complimentary Wi-Fi throughout your stay',
  'All meals and beverages included on cruise',
  'Kids club and family activities',
  'Access to resort pools, fitness center, and spa facilities',
  'Daily housekeeping and concierge services',
  'Access to entertainment and shows',
  '24/7 room service available',
];

function ProductIcon({ type, className }: { type: string; className?: string }): JSX.Element {
  const cls = className ?? styles.iconSm;
  if (type.toLowerCase() === 'cruise') return <FontAwesomeIcon icon={faShip} className={cls} />;
  if (type.toLowerCase() === 'hotel') return <FontAwesomeIcon icon={faHotel} className={cls} />;
  return <FontAwesomeIcon icon={faHome} className={cls} />;
}

export function LayoutImmersive({ data }: LayoutImmersiveProps): JSX.Element {
  const title = data.customHeadline ?? data.templateName;
  const tagline = data.customSubheadline ?? '';
  const ctaText = data.customCtaText ?? 'Buy Certificate';
  const heroImage = data.templateImageUrl ?? LANDING_DEFAULT_HERO_IMAGE;
  const savingsPercent =
    data.averageRetailPrice > 0 ? Math.round((data.savings / data.averageRetailPrice) * 100) : 0;
  const totalNights = data.products.reduce((acc, p) => acc + p.nights, 0);
  const hasCruise = data.products.some((p) => p.type.toLowerCase() === 'cruise');
  const resortAmenityLines = DEFAULT_RESORT_AMENITY_LINES.map((line) =>
    line === 'All meals and beverages included on cruise' && !hasCruise
      ? 'Dining and beverage options per your package inclusions'
      : line
  );
  const amenityChecklistLines: string[] = [
    ...data.products.map((product) => {
      const typeLabel =
        product.type.charAt(0).toUpperCase() + product.type.slice(1).toLowerCase();
      return `${product.nights}-Night ${typeLabel} included`;
    }),
    ...resortAmenityLines,
  ];

  /** Two matching resort/vacation shots (same frame, no mixed “dining” vs pool styles). */
  const amenityPairImages: [string, string] = [
    LANDING_DEFAULT_PRODUCT_IMAGES.hotel,
    LANDING_DEFAULT_PRODUCT_IMAGES.condo,
  ];
  // RSI: parent sets customCtaUrl; do not force BO payment-path fallback
  const ctaHref = data.customCtaUrl;

  return (
    <div className={styles.root}>
      <section className={styles.hero}>
        <div className={styles.heroMedia}>
          <img src={heroImage} alt="Luxury vacation destination" />
          <div className={styles.heroOverlayX} />
          <div className={styles.heroOverlayY} />
        </div>

        <div className={`container ${styles.heroInner}`}>
          <div className={styles.heroGrid}>
            <div className={styles.heroCopy}>
              {savingsPercent > 0 && (
                <span className={styles.savingsBadge}>Save up to {savingsPercent}%</span>
              )}
              <h1 className={styles.heroTitle}>{title}</h1>
              {tagline && <p className={styles.heroTagline}>{tagline}</p>}
              {data.templateDescription && (
                <p className={styles.heroDescription}>{data.templateDescription}</p>
              )}

              <div className={styles.productTags}>
                {data.products.map((product, index) => (
                  <div key={index} className={styles.productTag}>
                    <ProductIcon type={product.type} />
                    <span>
                      {product.type} &bull; {product.nights} nights
                    </span>
                  </div>
                ))}
              </div>

              <p className={styles.heroMeta}>
                <FontAwesomeIcon icon={faClock} className={styles.iconMd} />
                <span>Valid for {data.validityMonths} months</span>
              </p>
            </div>

            <div className={styles.priceCardWrap}>
              <div className={styles.priceCard}>
                <div className={styles.priceCardHeader}>
                  {data.averageRetailPrice > 0 && (
                    <p className={styles.priceCardStrike}>
                      ${data.averageRetailPrice.toLocaleString()}
                    </p>
                  )}
                  <p className={styles.priceCardTotal}>${data.totalPrice.toLocaleString()}</p>
                  {data.savings > 0 && (
                    <p className={styles.priceCardSavings}>
                      You save ${data.savings.toLocaleString()}
                    </p>
                  )}
                </div>

                {data.products.length > 0 && (
                  <div className={styles.priceLines}>
                    {data.products.map((product, index) => (
                      <div key={index} className={styles.priceRow}>
                        <span className={styles.priceRowLabel}>
                          {product.name} &times; {product.nights} nights
                        </span>
                      </div>
                    ))}
                    <div className={styles.priceTotal}>
                      <div className={styles.priceTotalRow}>
                        <span>Total (inc. taxes)</span>
                        <span>${data.totalPrice.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}

                <a href={ctaHref} className={styles.ctaButton}>
                  {ctaText}
                  <FontAwesomeIcon icon={faArrowRight} className={styles.iconMd} />
                </a>

                <p className={styles.priceCardFootnote}>
                  Secure checkout &bull; Instant delivery
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {data.customItems && data.customItems.length > 0 && (
        <section className={styles.gallerySection}>
          <div className="container">
            <div className={styles.galleryHeader}>
              <h2 className={styles.galleryTitle}>Dream Destinations Await</h2>
              <p className={styles.gallerySubcopy}>
                Choose from breathtaking locations around the world. Your certificate opens doors to
                paradise.
              </p>
            </div>

            <div className={styles.galleryGrid}>
              {data.customItems.map((item, index) => {
                const isFeatured = index === 0;
                return (
                  <div
                    key={index}
                    className={`${styles.galleryCard} ${
                      isFeatured ? styles.galleryCardFeatured : ''
                    }`}
                  >
                    <div
                      className={`${styles.galleryMedia} ${
                        isFeatured ? styles.galleryMediaFeatured : ''
                      }`}
                    >
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.title} />
                      ) : (
                        <div className={styles.galleryPlaceholder}>
                          <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.iconLg} />
                        </div>
                      )}
                      <div className={styles.galleryOverlay} />
                      <div className={styles.galleryCaption}>
                        <div className={styles.galleryCaptionInner}>
                          <FontAwesomeIcon
                            icon={faMapMarkerAlt}
                            className={`${styles.iconSm} ${styles.galleryCaptionIcon}`}
                          />
                          <div>
                            <h3 className={styles.galleryCardTitle}>{item.title}</h3>
                            {(item.description ?? item.subtitle) && (
                              <p className={styles.galleryCardBody}>
                                {item.description ?? item.subtitle}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <ExperiencesGridSection data={data} sectionClassName="bg-white" />

      {data.products.length > 0 && (
        <section className={styles.amenities}>
          <div className="container">
            <div className={styles.amenitiesGrid}>
              <div>
                <h2 className={styles.amenitiesTitle}>
                  Everything You Need for the Perfect Getaway
                </h2>
                {data.customDescription ? (
                  <div
                    className={`lp-richtext lp-richtext--on-brand ${styles.richtextSpacing}`}
                    dangerouslySetInnerHTML={{ __html: data.customDescription }}
                  />
                ) : (
                  <p className={styles.amenitiesIntro}>
                    Your certificate includes {totalNights} nights across {data.products.length}{' '}
                    vacation experience{data.products.length !== 1 ? 's' : ''}, with premium amenities
                    designed to make your stay unforgettable.
                  </p>
                )}
                <ul className="lp-amenity-list" role="list">
                  {amenityChecklistLines.map((line, index) => (
                    <li key={index}>
                      <div className={styles.amenityCheck}>
                        <FontAwesomeIcon icon={faCheck} className={styles.iconSm} aria-hidden />
                      </div>
                      <span className={styles.amenityCheckLabel}>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={styles.amenitiesAside}>
                <div className={styles.amenityPhotos}>
                  {amenityPairImages.map((src, i) => (
                    <div key={i} className={styles.amenityPhoto}>
                      <img src={src} alt="" loading="lazy" />
                    </div>
                  ))}
                </div>
                <div className={styles.packageSummary}>
                  <div className={styles.packageSummaryTitle}>Package Summary</div>
                  <div className={styles.packageSummaryLines}>
                    {data.products.map((product, index) => (
                      <div key={index} className={styles.packageSummaryRow}>
                        <div className={styles.packageSummaryLabel}>
                          <ProductIcon
                            type={product.type}
                            className={`${styles.iconSm} ${styles.packageSummaryIcon}`}
                          />
                          <span className={styles.packageSummaryName}>
                            {product.type} ({product.nights} nights)
                          </span>
                        </div>
                      </div>
                    ))}
                    <div className={styles.packageSummaryTotal}>
                      <div className={styles.packageSummaryTotalRow}>
                        <span>Your Price</span>
                        <span>${data.totalPrice.toLocaleString()}</span>
                      </div>
                      {data.savings > 0 && (
                        <div className={styles.saveBanner}>
                          Save ${data.savings.toLocaleString()} ({savingsPercent}% off retail)
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <CruiseLinesSection data={data} />
      <TestimonialsSection data={data} />

      <div className={styles.stickyCta}>
        <div className={`container ${styles.stickyCtaInner}`}>
          <div className={styles.stickyCopy}>
            <p className={styles.stickyCopyTitle}>{title}</p>
            <p className={styles.stickyCopyMeta}>
              {data.products.length} vacation package{data.products.length !== 1 ? 's' : ''} included
            </p>
          </div>
          <div className={styles.stickyPriceBlock}>
            <div className={styles.stickyPrice}>
              {data.averageRetailPrice > 0 && (
                <p className={styles.stickyPriceStrike}>
                  ${data.averageRetailPrice.toLocaleString()}
                </p>
              )}
              <p className={styles.stickyPriceValue}>${data.totalPrice.toLocaleString()}</p>
            </div>
            <a href={ctaHref} className={styles.stickyCtaButton}>
              {ctaText}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
